import { useState } from "react";
import { Button } from "@/components/ui/button";

export function ListPagination({ total, page, setPage, pageSize = 20, noun = "records", testPrefix = "list" }) {
  if (!total || total <= pageSize) return null;
  const pages = Math.ceil(total / pageSize);
  const cur = Math.min(page, pages);
  const nums = Array.from({ length: pages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === pages || Math.abs(p - cur) <= 2)
    .reduce((acc, p) => {
      if (acc.length && p - acc[acc.length - 1] > 1) acc.push("…");
      acc.push(p);
      return acc;
    }, []);
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3" data-testid={`${testPrefix}-pagination`}>
      <span className="text-xs text-muted-foreground">
        Showing {(cur - 1) * pageSize + 1}–{Math.min(cur * pageSize, total)} of {total} {noun}
      </span>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" className="h-8" disabled={cur === 1} onClick={() => setPage(cur - 1)} data-testid={`${testPrefix}-prev-page`}>
          Prev
        </Button>
        {nums.map((p, i) =>
          p === "…" ? (
            <span key={`e${i}`} className="px-1 text-xs text-muted-foreground">…</span>
          ) : (
            <Button
              key={p}
              variant={p === cur ? "default" : "outline"}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setPage(p)}
              data-testid={`${testPrefix}-page-${p}`}
            >
              {p}
            </Button>
          )
        )}
        <Button variant="outline" size="sm" className="h-8" disabled={cur === pages} onClick={() => setPage(cur + 1)} data-testid={`${testPrefix}-next-page`}>
          Next
        </Button>
      </div>
    </div>
  );
}

export function PagedList({ items, pageSize = 20, noun = "records", testPrefix = "list", children }) {
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  const cur = Math.min(page, pages);
  const offset = (cur - 1) * pageSize;
  return (
    <>
      {children(items.slice(offset, offset + pageSize), offset)}
      <ListPagination total={items.length} page={page} setPage={setPage} pageSize={pageSize} noun={noun} testPrefix={testPrefix} />
    </>
  );
}

const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};

export function NewBadge({ createdAt }) {
  if (!isToday(createdAt)) return null;
  return (
    <span className="ml-1.5 inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-700" data-testid="new-today-badge">
      New
    </span>
  );
}
